import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal, viewChild } from '@angular/core';
import {TemplateEditorComponent} from '@app/_components/template-editor/template-editor.component';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {lastValueFrom, Subject} from 'rxjs';
import {InfluencerType, SocialImageSize, User} from '@app/modules/shared/models';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {
  CampaignUpdateSuccess,
  getInfluencerError,
  getInfluencersTypes, getLoggedInUser,
  getSocialImageSizes,
  getTemplate,
  getTemplateError,
  getTemplatePreviewUrl, getTemplates,
  getTemplateSectionError,
  getTemplateSections,
  getTemplateSectionTypes, getTemplateSuccess, getTemplateTypes,
  getVariableError,
  getVariables, IAuthenticationState, ICampaignState,
  IInfluencersState,
  InfluencersTypeList,
  ITemplateSectionState,
  ITemplateState,
  IVariableState,
  ResetInfluencerState, ResetTemplateSectionState,
  ResetTemplateState,
  TemplateCreate,
  TemplateGet, TemplateGetSocialImageSizes, TemplateGetTypes, TemplateListAll,
  TemplateSectionListAll,
  TemplateSectionTypeList,
  TemplateUpdate,
  VariableList
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, Router} from '@angular/router';
import {SocketService} from '@app/services/socket.service';
import {takeUntil} from 'rxjs/operators';
import {LANDING_PAGE_TEMPLATE_TYPE_ID, SOCIAL_MEDIA_TEMPLATE_TYPE_ID, VIDEO_TEMPLATE_TYPE_ID} from '@app/consts';
import * as _ from 'lodash';
import * as routes from '@app/routes';
import {TemplateGeneratorService} from '@app/services/template-generator.service';
import {TemplateRevisionsComponent} from '@app/_components/template-revisions/template-revisions.component';
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {LoaderService} from '@app/services/loader.service';
import {NewEditorComponent} from '../new-editor';
import {CampaignService} from '@app/services/campaign.service';
import {startPanelResize} from '@app/_helpers/panel-resize';
import { NewEditorComponent as NewEditorComponent_1 } from '../new-editor/index';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { StatusBarComponent } from '../../status-bar/status-bar.component';
import { TemplateEditorComponent as TemplateEditorComponent_1 } from '../../../_components/template-editor/template-editor.component';
import { AsyncPipe } from '@angular/common';
import { DeviceToolbarComponent } from '../../../_components/device-toolbar/device-toolbar.component';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

type SaveTemplateForm = FormGroup<{
  saveAsTemplateName: FormControl<string | null>;
  influencer_type_id: FormControl<number | string | null>;
}>;

const STYLE_KEYS = ['image_width', 'image_height', 'image_aspect_ratio', 'predefined_size_id', 'max_width', 'margin_top',
  'background_color', 'font_color', 'header_font_color', 'link_color', 'button_color', 'button_font_color',
  'button_border_color', 'button_hover_color', 'font_family', 'header_font_family', 'custom_css'] as const;

// The sizes arrive as numbers from the template and as text from the fields.
type SocialStylesForm = FormGroup<Record<typeof STYLE_KEYS[number], FormControl<string | number | null>>>;

@Component({
  selector: 'app-social-share-editor',
  templateUrl: './index.html',
  styleUrls: ['./index.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NewEditorComponent_1, PageShellComponent, StatusBarComponent, TemplateEditorComponent_1, DeviceToolbarComponent, ReactiveFormsModule, AsyncPipe, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu]
})
export class SocialShareEditorComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private templateSectionStore = inject<Store<ITemplateSectionState>>(Store);
  private templateStore = inject<Store<ITemplateState>>(Store);
  private variableStore = inject<Store<IVariableState>>(Store);
  private influencersStore = inject<Store<IInfluencersState>>(Store);
  private toastr = inject(ToastrService);
  private activatedRoute = inject(ActivatedRoute);
  private campaignStore = inject<Store<ICampaignState>>(Store);
  private router = inject(Router);
  private socketService = inject(SocketService);
  private loaderService = inject(LoaderService);
  private templateGeneratorService = inject(TemplateGeneratorService);
  private _modalService = inject(NgbModal);
  private readonly sbModal = inject(SbModalService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private campaignService = inject(CampaignService);
  // The page's fields, the template and its editor data, are written from the store, the
  // template generator service and dialogs' promises: each of those marks the view.
  private changeDetector = inject(ChangeDetectorRef);


  readonly templateEditor = viewChild<TemplateEditorComponent>('templateEditor');
  readonly newEditorRef = viewChild<NewEditorComponent>('newEditorRef');
  public step;
  public viewportSize = 'desktop';
  public template: any;
  // Set by the old editor's OnTemplateLoaded. The editor sits in an @if view, which Angular refreshes only
  // after this view's own bindings. When it is handed another page after the step loaded one (a revision
  // under Undo All, the campaign loaded again), it emits false from its ngOnChanges there, after Save was
  // checked. A signal has Angular refresh this view again in the same pass; a plain field left Save
  // enabled while the editor loaded the page (NG0100 in dev builds).
  readonly isTemplateLoaded = signal(false);
  public rawTemplatesTypes = [];
  public templates = [];
  public socialImageSizes: SocialImageSize[] = [];
  public stylesForm: SocialStylesForm;
  unsubscriber = new Subject();
  public variables = {};
  public templateId: number;
  private saveClick = false;
  private saveExitClick = false;
  submitted = false;
  influencerTypes: InfluencerType[] = [];
  subscription: any;
  projectId: number;
  campaignId: number;
  campaignMeta: any;
  campaign: any;
  saveAsTemplateName = '';
  saveAsTemplateNameSubmit = false;
  loader$: Observable<boolean>;
  currentUser: User;
  templateType = 'public';
  isSaveNewTemplate = false;
  newDummyInfluencerType: any = {};
  influencerTypeId: any;
  public templateForm: SaveTemplateForm;
  public newEditor = {
    show: false,
    active: false
  };
  // Nothing renders until the campaign says which editor it uses, so a campaign on the
  // visual editor never loads the old editor page first.
  public editorDecided = false;
  // Personalize Video edits the campaign's own share-video design instead of the share image's.
  public readonly isVideoMode: boolean;
  public templateData = {
    body: null,
    bodyJson: null,
    visualEditorId: null,
    campaignId: null,
    projectId: null,
    influencerTypeId: null,
    templateId: null,
    templateTypeId: null,
    style: null,
  };

  defaultPanelWidth = 700;
  panelWidth = this.defaultPanelWidth;
  panelCollapsed = false;

  constructor() {
    this.isVideoMode = this.activatedRoute?.snapshot?.data?.shareMedia === 'video';
    this.templateStore.dispatch(ResetTemplateState({params: {previewUrl: null, error: '', success: ''}}));
    this.templateStore.dispatch(ResetTemplateSectionState({params: {error: '', templateSectionTypes: [], templateSections: []}}));
    this.subscribeStore();
    this.initializeForms();
    this.subscription = this.socketService.sourceData
      .subscribe((message: any) => {
        if (message?.data?.requesterId) {
          if (message.data?.requesterType === 'section') {
            const temp = _.cloneDeep(this.templates);
            temp.map(element => {
              if (element.id === message.data.requesterId) {
                element.thumbnail = message.data.result.thumbnail;
              }
              return element;
            });
            this.templates = [...temp];
            this.changeDetector.markForCheck();
          }
        }
      },
      (err) => console.error(err),
      () => console.warn('Completed!')
      );
  }

  initializeForms() {
    this.stylesForm = this.formBuilder.group({
      // max_width: new FormControl(''),
      image_width: new FormControl<string | number | null>(''),
      image_height: new FormControl<string | number | null>(''),
      image_aspect_ratio: new FormControl<string | number | null>(''),
      predefined_size_id: new FormControl<string | number | null>(''),
      max_width: new FormControl<string | number | null>('1200'),
      margin_top: new FormControl<string | number | null>(''),
      background_color: new FormControl<string | number | null>(''),
      font_color: new FormControl<string | number | null>(''),
      header_font_color: new FormControl<string | number | null>(''),
      link_color: new FormControl<string | number | null>(''),
      button_color: new FormControl<string | number | null>(''),
      button_font_color: new FormControl<string | number | null>(''),
      button_border_color: new FormControl<string | number | null>(''),
      button_hover_color: new FormControl<string | number | null>(''),
      font_family: new FormControl<string | number | null>(''),
      header_font_family: new FormControl<string | number | null>(''),
      custom_css: new FormControl<string | number | null>(''),
    });
  }

  subscribeStore() {
    this.influencersStore.dispatch(ResetInfluencerState({params: {error: '', influencerTypes: []}}));
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser = user;
          this.changeDetector.markForCheck();
        }
      }
      );
    // this.templateSectionStoreSubscribe();
    this.templateStoreSubscribe();
    this.variableStoreSubscribe();
    this.influencerStoreSubscribe();
  }

  templateSectionStoreSubscribe() {
    this.templateSectionStore.dispatch(TemplateSectionTypeList({params: {}}));
    this.templateSectionStore.pipe(select(getTemplateSections))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateSections => {
        if (templateSections && templateSections.length > 0) {
          this.templates = templateSections;
          this.changeDetector.markForCheck();
        }
      });
    this.templateSectionStore.pipe(select(getTemplateSectionTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateSectionTypes => {
        if (templateSectionTypes && templateSectionTypes.length > 0) {
          this.rawTemplatesTypes = templateSectionTypes;
          this.changeDetector.markForCheck();
          this.fetchRawTemplatesById(this.rawTemplatesTypes.filter(x => x.name === 'social images')[0].id);
        }
      });
    this.templateSectionStore.pipe(select(getTemplateSectionError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    this.templateStore.pipe(select(getTemplatePreviewUrl))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(previewUrl => {
        if (previewUrl && previewUrl.url) {
          window.open(previewUrl.url, '_blank');
        }
      });
  }

  templateStoreSubscribe() {
    this.templateStore.pipe(select(getTemplates))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templates => {
        if (templates && templates.length > 0) {
          this.templates = templates;
          this.changeDetector.markForCheck();
        }
      });
    this.templateStore.pipe(select(getTemplateTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateTypes => {
        if (templateTypes && templateTypes.length > 0) {
          this.rawTemplatesTypes = templateTypes;
          this.changeDetector.markForCheck();
        }
      });
    this.templateStore.pipe(select(getTemplateError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
          if (this.saveClick || this.saveExitClick) {
            this.onSaveFailed();
          }
        }
      });
    this.templateStore.pipe(select(getTemplate))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(template => {
        if (template && !this.isSaveNewTemplate) {
          this.templateId = template.id;
          if (!this.saveClick) {
            this.replaceTemplate(template);
          }
          if (this.saveClick || this.saveExitClick) {
            const saveExit = this.saveExitClick;
            this.saveExitClick = false;
            this.saveClick = false;
            this.submitted = false;
            this.handlePersonalizeImageSaveOutcome(saveExit);
            this.changeDetector.markForCheck();
            return;
          }
          this.saveExitClick = false;
          this.saveClick = false;
          this.submitted = false;
          this.changeDetector.markForCheck();
        } else if (template && this.isSaveNewTemplate) {
          this.fetchRawTemplates();
          this.isSaveNewTemplate = false;
        }
      });
    this.templateStore.pipe(select(getSocialImageSizes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(socialImageSizes => {
        if (socialImageSizes && socialImageSizes.length > 0) {
          this.socialImageSizes = socialImageSizes;
          this.changeDetector.markForCheck();
        }
      });
  }

  fetchRawTemplates() {
    if (this.newEditor.active) {
      return;
    }
    const orderBy = {column: 'updated_at', dir: 'desc'};
    let options = {};
    if (!this.currentUser?.can('view-advanced-editor', 'template')) {
      options['includeOnlyVisible'] = true;
    }
    setTimeout(() => {
      options = {...options, includeClientTemplates: this.campaign.client_id, includeInfluencerTemplates: this.campaign.segments[0].influencer_type_id, orderBy};

      this.templateStore.dispatch(TemplateListAll({
        params: {
          options: JSON.stringify(options),
          template_type_id: SOCIAL_MEDIA_TEMPLATE_TYPE_ID,
          // influencer_type_id: this.campaign.segments[0].influencer_type_id,
          project_id: this.campaign.project_id,
        }
      }));
    }, 500);
  }

  influencerStoreSubscribe() {
    this.influencersStore.pipe(select(getInfluencerError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.influencersStore.pipe(select(getInfluencersTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencerTypes => {
        if (influencerTypes && influencerTypes.length > 0) {
          this.newDummyInfluencerType.id = null;
          this.newDummyInfluencerType.name = 'All';
          this.influencerTypes = [
            this.newDummyInfluencerType,
            ...influencerTypes
          ];
          this.changeDetector.markForCheck();
        }
      });
  }

  variableStoreSubscribe() {
    this.variableStore.pipe(select(getVariableError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    this.variableStore.pipe(select(getVariables))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(variables => {
        if (variables) {
          this.variables = variables;
          this.changeDetector.markForCheck();
        }
      });
  }

  replaceTemplate(template) {
    if (template) {
      template = {...template};
      if (!template.style) {
        template.style = '<style></style>';
      }
      if (!template.body && !template.visual_editor_id) {
        template.body = `<header data-block-type="social_images" data-id="2" class="fdb-block fp-active" draggable="false">
                    <div class="container">
                    <div class="row">
                    <div class="col-md-12">Edit here...</div>
                    </div>
                    </div>
                </header>`;
      }

      this.template = template;
      const t = template || {};

      const formFields = {};
      let properties = t.properties;
      if (properties) {
        while (typeof properties === 'string') {
          properties = JSON.parse(properties);
        }
        Object.entries(this.stylesForm.controls).forEach(([key, val]) => {
          formFields[key] = properties.hasOwnProperty(key) ? properties[key] : '';
        });
        formFields['max_width'] = 1200;
        this.stylesForm.patchValue(formFields);
      }

      formFields['image_width'] = template.image_width ? template.image_width : '';
      formFields['image_height'] = template.image_height ? template.image_height : '';
      formFields['image_aspect_ratio'] = template.image_aspect_ratio ? template.image_aspect_ratio : '';
      this.stylesForm.patchValue(formFields);
      // Also from the revisions dialog's promise.
      this.changeDetector.markForCheck();
    }
  }

  ngOnInit(): void {
    this.loader$ = this.loaderService.loader$;
    this.templateForm = this.formBuilder.group({
      saveAsTemplateName: new FormControl<string | null>('', [Validators.required]),
      influencer_type_id: new FormControl<number | string | null>(null),
    });
    this.variableStore.dispatch(VariableList({params: {}}));
    this.influencersStore.dispatch(InfluencersTypeList());
    this.templateGeneratorService.forceReloadCampaign();
    this.templateGeneratorService.subscribeStore();
    this.templateGeneratorService.onCampaignLoaded
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(async campaign => {
        if (!campaign) {
          return;
        }
        this.campaign = campaign;
        this.initStep();
        if (campaign.meta) {
          this.campaignMeta = (typeof campaign.meta === 'string') ? {...JSON.parse(campaign.meta)} : {...campaign.meta};
        }
        this.projectId = campaign.project_id;
        this.campaignId = campaign.id;

        const savedTemplate = this.isVideoMode ? this.campaign.social_video_template : this.campaign.social_template;
        if (savedTemplate) {
          this.templateId = savedTemplate.id;
          this.replaceTemplate(savedTemplate);
        } else {
          // A campaign without a social image starts in the Social Image Editor: no
          // visual_editor_id, so the editor page opens the type's default editor.
          this.templateId = null;
          this.template = {
            body: null,
            body_json: null,
            visual_editor_id: null,
            campaign_id: this.campaign.id,
            influencer_type_id: this.campaign.segments?.[0]?.influencer_type_id ?? null,
            template_type_id: this.isVideoMode ? VIDEO_TEMPLATE_TYPE_ID : SOCIAL_MEDIA_TEMPLATE_TYPE_ID
          };
        }

        this.templateData = {
          visualEditorId: this.template.visual_editor_id ? this.template.visual_editor_id : null,
          body: this.template.body,
          bodyJson: this.template.body_json,
          style: this.template.style || null,
          campaignId: this.campaign.id,
          projectId: this.campaign.project_id || null,
          influencerTypeId: this.template.influencer_type_id,
          templateId: this.templateId,
          templateTypeId: this.editorTemplateTypeId()
        };

        this.editorDecided = true;
        this.changeDetector.markForCheck();
        if (this.templateData.visualEditorId || !savedTemplate) {
          this.showNewEditor();
          return;
        }

        this.newEditor.active = false;
        this.newEditor.show = false;
        this.loadLegacyEditorData();
      });
  }

  // Only the old editor needs the template types, image sizes and the template gallery.
  loadLegacyEditorData() {
    this.templateSectionStore.dispatch(TemplateGetTypes());
    this.templateSectionStore.dispatch(TemplateGetSocialImageSizes());
    this.fetchRawTemplates();
  }

  loadTemplate(templateId) {
    this.templateStore.dispatch(TemplateGet({templateId}));
  }

  templateLoaded(event) {
    this.isTemplateLoaded.set(event);
  }

  onStylesCustomizationChange(params) {
    this.stylesForm.patchValue({...params});
  }

  onPageSizeSelect(event) {
    this.viewportSize = event;
  }

  onEditHtml() {
    this.templateEditor().onEditHtml();
  }

  onBackButtonClick() {
    const route = routes.campaign({projectId: this.projectId, campaignId: this.campaignId});
    this.router.navigateByUrl(`/${route}/share`);
  }

  async buildTemplateForSave() {
    let template: any = {};
    if (this.template) {
      template = {...this.template};
    }

    template.properties = {};

    if (!this.newEditor.active) {
      const htmlParts = await this.templateEditor().buildTemplateForSave();
      const {value} = this.stylesForm;

      Object.entries(value).forEach(([key, val]) => {
        if (key === 'image_width' || key === 'image_height' || key === 'image_aspect_ratio') {
          return;
        }
        template.properties[key] = val;
      });

      if (value.image_width) {
        template.width = value.image_width;
      }
      if (value.image_height) {
        template.height = value.image_height;
      }
      if (value.image_aspect_ratio) {
        template.aspect_ratio = value.image_aspect_ratio;
      }

      let htmlBody: any = htmlParts.body;
      htmlBody = htmlBody.replaceAll(' fp-active', '');
      htmlBody = htmlBody.replaceAll('fp-active', '');
      template.body = htmlBody.replaceAll(/data-id="[^"]*"/g, '');
      template.body_json = null;
      template.visual_editor_id = null;
      template.style = htmlParts.style;
    } else {
      template.visual_editor_id = this.templateData.visualEditorId;
      template.body = this.templateData.body;
      template.body_json = this.templateData.bodyJson;
      template.style = null;
      // The share page's og:image size comes from the template, not from the canvas.
      const canvas = this.canvasOf(this.templateData.bodyJson);
      if (canvas) {
        template.width = canvas.width;
        template.height = canvas.height;
      }
    }
    template.name = this.isVideoMode
      ? `${this.campaign.id}-Personalized Share Video`
      : `${this.campaign.id}-Personalized Share Image`;
    template.template_type_id = this.isVideoMode ? VIDEO_TEMPLATE_TYPE_ID : SOCIAL_MEDIA_TEMPLATE_TYPE_ID;
    template.influencer_type_id = this.campaign.segments[0].influencer_type_id;
    template.client_id = this.campaign.client_id;
    template.project_id = this.campaign.project_id;
    template.campaign_id = this.campaign.id;

    delete template.content_hash;
    return template;
  }

  // The video design is edited as a social image: the editor page picks its editor by that type.
  private editorTemplateTypeId() {
    return this.isVideoMode ? SOCIAL_MEDIA_TEMPLATE_TYPE_ID : this.template.template_type_id;
  }

  private canvasOf(bodyJson: any): {width: number; height: number} | null {
    let doc = bodyJson;
    if (typeof doc === 'string') {
      try {
        doc = JSON.parse(doc);
      } catch {
        return null;
      }
    }
    const width = Number(doc?.canvas?.width);
    const height = Number(doc?.canvas?.height);
    return width > 0 && height > 0 ? {width, height} : null;
  }

  showTemplateRevisions() {
    const modelRef = this.sbModal.openComponent(TemplateRevisionsComponent, {centered: true, keyboard: true, size: 'xl'}, {
      templateId: this.template?.id,
      canReset: false,
    });
    modelRef.result.then((result) => {
      if (result.data) {
        this.templateEditor().disableEditor();
        this.replaceTemplate(result.data);
      }
    }, (reason) => {
    });
  }

  private persistCampaignImageTemplate(): Promise<boolean> {
    return this.persistCampaignTemplateFlags({image_template: true, image: null});
  }

  // The video design only becomes the share video's; the share image stays as it is.
  private persistCampaignVideoTemplate(): Promise<boolean> {
    return this.persistCampaignTemplateFlags({video_template: true});
  }

  // Fetches the campaign fresh and writes its meta back with the template flags set,
  // instead of the stale snapshot this editor loaded with -- Share Settings' own autosave
  // can land after this editor opened. Never navigates on failure; dispatches
  // CampaignUpdateSuccess the same way Share Settings does on success.
  private async persistCampaignTemplateFlags(flags: {[key: string]: any}): Promise<boolean> {
    try {
      const freshResp: any = await lastValueFrom(this.campaignService.getCampaign(this.campaign.id));
      const freshMeta = freshResp?.data?.meta;
      const meta = {
        ...(typeof freshMeta === 'string' ? JSON.parse(freshMeta) : (freshMeta || this.campaignMeta || {})),
        ...flags
      };

      const resp: any = await lastValueFrom(this.campaignService.updateCampaign({ meta }, this.campaign.id));
      if (!resp?.success) {
        this.toastr.error(this.loaderService.getErrorMessage(resp));
        return false;
      }

      const savedMeta = resp.data?.meta;
      this.campaignMeta = typeof savedMeta === 'string' ? JSON.parse(savedMeta) : { ...(savedMeta || meta) };
      this.campaignStore.dispatch(CampaignUpdateSuccess({ campaign: resp.data }));
      return true;
    } catch (error) {
      this.toastr.error(this.loaderService.getErrorMessage(error));
      return false;
    }
  }

  // Navigates back to Share, or to Campaigns on save-and-exit, only once the campaign
  // update actually succeeds. On failure it stays put -- the persist already showed the toastr.
  private handlePersonalizeImageSaveOutcome(saveExit: boolean): void {
    const persist = this.isVideoMode ? this.persistCampaignVideoTemplate() : this.persistCampaignImageTemplate();
    persist.then(success => {
      if (!success) {
        this.onSaveFailed();
        return;
      }
      if (saveExit) {
        this.router.navigateByUrl('/' + routes.campaigns({ projectId: this.campaign.project_id }));
      } else {
        this.onBackButtonClick();
      }
    });
  }

  async saveClientTemplate() {
    this.isSaveNewTemplate = false;
    this.submitted = true;
    this.saveClick = true;
    if (this.templateId) {
      this.templateStore.dispatch(TemplateUpdate({
        id: this.templateId,
        body: await this.buildTemplateForSave()
      }));
    } else {
      this.templateStore.dispatch(TemplateCreate({
        body: await this.buildTemplateForSave()
      }));
    }
  }

  fetchRawTemplatesById(id) {
    this.templateSectionStore.dispatch(TemplateSectionListAll({
      params: {
        options: JSON.stringify({includeOnlyVisible: true}),
        template_section_type_id: id,
        template_type_id: LANDING_PAGE_TEMPLATE_TYPE_ID
      }
    }));
  }

  async saveAsNewTemplateModel(template, type) {
    this.templateForm.patchValue({influencer_type_id: this.campaign.segments[0].influencer_type_id});
    this.templateType = type;
    const modelRef = this._modalService.open(template, {
      centered: false,
      size: 'sm',
      keyboard: true
    });
    modelRef.result.then((result) => {
    }, (reason) => {
    });
  }

  async saveAsNewTemplate(modal: any) {
    this.isSaveNewTemplate = true;
    this.saveAsTemplateNameSubmit = true;
    this.saveAsTemplateName = this.templateForm.get('saveAsTemplateName').value;
    if (this.templateForm.get('influencer_type_id').value === 'null') {
      this.templateForm.patchValue({influencer_type_id: null});
    }
    this.influencerTypeId = this.templateForm.get('influencer_type_id').value;
    if (this.saveAsTemplateName) {
      const template = await this.buildTemplateForSave();
      modal.close();
      let extraParams;
      if (this.templateType === 'client') {
        extraParams = {
          name: this.saveAsTemplateName,
          client_id: this.campaign.client_id,
          project_id: this.campaign.project_id,
          campaign_id: null,
          influencer_type_id: this.influencerTypeId,
        };
      } else {
        extraParams = {
          name: this.saveAsTemplateName,
          client_id: null,
          project_id: null,
          campaign_id: null,
          influencer_type_id: this.influencerTypeId
        };
      }
      if (this.isVideoMode) {
        // There is no library of video designs: a saved copy is a social image template.
        extraParams.template_type_id = SOCIAL_MEDIA_TEMPLATE_TYPE_ID;
      }
      const sourceTemplateId = template.id;
      delete template.id;
      this.templateGeneratorService.reloadTemplate = false;
      this.templateStore.dispatch(TemplateCreate({
        body: {...template, ...extraParams, source_template_id: sourceTemplateId}
      }));
    }
  }

  changingInfluencerType(e) {
    this.templateForm.patchValue({influencer_type_id: e});
  }

  onSaveButtonClick(saveExitClick = true) {
    this.saveExitClick = saveExitClick;
    this.saveClientTemplate();
  }

  initStep() {
    this.step = {
      title: 'Share',
      params: {
        projectId: this.campaign?.project_id,
        campaignId: this.campaign?.id,
        campaignName: this.campaign?.name
      }
    };
  }

  // A template without a visual_editor_id opens in the type's default editor, which the
  // editor page picks and reports back with the save.
  showNewEditor() {
    this.newEditor.active = true;
    this.newEditor.show = true;
  }

  // The visual editor is closed while it saves; a failed save opens it again with the
  // document the save carried, so nothing typed is lost.
  private onSaveFailed(): void {
    this.saveClick = false;
    this.saveExitClick = false;
    this.submitted = false;
    // From the store's error, or the campaign update's promise.
    this.changeDetector.markForCheck();
    if (this.newEditor.active && !this.newEditor.show) {
      this.templateData = {...this.templateData, templateId: this.templateId ?? this.templateData.templateId};
      this.newEditor.show = true;
    }
  }

  async onEditorStateChanged(event: { action: string; body: any; bodyJson: string; visualEditorId: any }) {
    if (event.action === 'save') {
      this.templateData = {
        body: event.body,
        bodyJson: event.bodyJson ? JSON.parse(event.bodyJson) : null,
        visualEditorId: (event.visualEditorId && event.visualEditorId !== 'null') ? event.visualEditorId : null,
        campaignId: this.template.campaign_id,
        projectId: this.campaign?.project_id || null,
        influencerTypeId: this.template.influencer_type_id,
        templateId: this.template.id,
        templateTypeId: this.editorTemplateTypeId(),
        style: null,
      };
      this.newEditor.active = true;
      this.newEditor.show = false;
      await this.saveClientTemplate();
      return;
    }
    if (event.action === 'undo-all') {
      this.showNewEditorTemplateRevisions();
      return;
    }
    this.newEditor.show = false;
    this.onBackButtonClick();
  }

  showNewEditorTemplateRevisions() {
    const modelRef = this.sbModal.openComponent(TemplateRevisionsComponent, {
      centered: true, keyboard: true, size: 'xl',
      windowClass: 'above-editor-modal',
      backdropClass: 'above-editor-modal-backdrop'
    }, {
      templateId: this.template?.id,
      canReset: false,
    });
    modelRef.result.then((result) => {
      const newEditorRef = this.newEditorRef();
      if (result.data && newEditorRef) {
        newEditorRef.loadTemplate(result.data);
      }
    }, () => {});
  }

  startResizing(event: PointerEvent) {
    startPanelResize(event, this.panelWidth, width => {
      this.panelWidth = width;
      this.changeDetector.markForCheck();
    });
  }

  togglePanel() {
    this.panelCollapsed = !this.panelCollapsed;
    if (this.panelCollapsed) {
      this.panelWidth = 25;
    } else {
      this.panelWidth = this.defaultPanelWidth;
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    this.subscription.unsubscribe();
  }
}
